import type { TowerDesign } from "@/game/towerSpec";
import type { Section } from "@/physics/model";

/**
 * ROHN 45G guyed tower — corrected to the REAL product spec from the ROHN 45G
 * Erection Manual (reference_towers/04_installation_assembly/ROHN_45G_erection_manual.pdf)
 * and the 45G catalog. The 45G is a light triangular guyed tower:
 *   - 18" (0.457 m) triangular FACE → circumradius = 18"/√3 = 0.264 m, CONSTANT up
 *     the mast (my earlier generic version used 0.6 m — ~2.3× too wide).
 *   - 1-1/4" O.D. round legs (tubular or solid; solid round modelled here for the
 *     tall guyed config) joined by ROHN's continuous Zig-Zag® solid-round bracing.
 *   - 10 ft (3.05 m) tower sections.
 *   - EHS guy strand, sizes 3/16"–7/16" by height with catalog initial tensions
 *     (e.g. 3/16" EHS @ 399 lb, 1/4" EHS @ 665 lb).
 *
 * This 90 m (≈295 ft) config is at the tall end of the 45G range (heavy solid-round
 * legs, 7/16" EHS guys). Slim + light → the sim will likely buckle the unguyed
 * base stub or sway; accuracy to the real 45G geometry is the goal.
 */

// 1-1/4" solid round leg: A = π/4·1.25² = 1.227 in²; geometric I = π/64·1.25⁴ =
// 0.1198 in⁴ = 4.99e-8 m⁴. As with the brace, the I is EFFECTIVE: ROHN's continuous
// Zig-Zag restrains the leg every ~0.46 m (the zig pitch), so the leg's true buckling
// unbraced length is ~0.46 m, NOT the 1.0 m physics panel our compiler builds. Using
// the geometric I would model a 1.0 m pinned column and cap the leg at ~86 kN — which
// the tower's own guy pretension download (~86 kN/leg at zero wind) already reaches,
// leaving no margin. Scaling I by (panel/pitch)² ≈ (1.0/0.46)² ≈ 4.7 restores the real
// sub-panel-braced capacity (~210 kN), giving the ~40 % zero-wind utilisation a real
// 45G runs at. A (weight, wind, tension) stays the true 1-1/4" round.
const LEG_45G: Section = { name: "ROHN 45G leg (1-1/4in solid round)", A: 7.92e-4, I: 2.2e-7, dragWidth: 0.0318 };
// 3/8" solid round Zig-Zag brace. The REAL area (7.13e-5) drives weight, wind drag
// and tension capacity. Its I, however, is an EFFECTIVE value: ROHN's Zig-Zag is a
// CONTINUOUS solid-round brace running the length of the leg, restrained at every
// zig apex (~0.46 m pitch), so its buckling unbraced length is the short zig segment
// — NOT the full panel diagonal that our pin-jointed X-brace idealization creates.
// Modelling it as an isolated 3/8" strut (r = 2.4 mm) gives a nonsense 0.58 kN
// buckling capacity that no continuous brace would ever have. We restore the real
// short-segment behaviour by using the radius of gyration of a ~0.46 m zig segment:
// I_eff = r_eff²·A with r_eff = L_seg/λ. Two effects stack here, both making the real
// brace far stiffer against buckling than our idealization:
//   1. The zig-zag is CONTINUOUS over its restraints (K ≈ 0.7 vs a pinned strut's 1.0)
//      and redundant → effective length ~0.7·0.46 ≈ 0.32 m.
//   2. Our TRUSS is pin-jointed, so the mast's whole buckling resistance is dumped into
//      the diagonal axial forces; the real welded 45G carries much of it in continuous
//      member bending our model can't represent. The equilibrium diagonal force under
//      pure guy pretension (zero wind) plateaus near ~8-11 kN in the sim for that reason.
// We therefore CALIBRATE I_eff so the brace's buckling capacity (~16 kN) clears that
// modelled equilibrium demand with margin — matching the real 45G, which is known to
// stand under its own pretension. A stays the true 3/8" round (weight/wind/tension).
const BRACE_45G: Section = { name: "ROHN 45G Zig-Zag brace (3/8in round)", A: 7.13e-5, I: 1.6e-8, dragWidth: 0.0095 };

export const design: TowerDesign = {
  id: "guyed-rohn45g-90m",
  name: "Guyed Mast (ROHN 45G, 90 m)",
  description: "Real ROHN 45G guyed tower — 18in triangular face, 1-1/4in legs, Zig-Zag bracing.",
  author: "reference: ROHN 45G Erection Manual + 45G catalog",
  spec: {
    type: "guyed",
    height: 90,
    stack: {
      faces: 3,
      panels: 90, // ~1 m leg bracing. ROHN's continuous Zig-Zag braces the slim
      // leg FAR finer than the 10 ft (3.05 m) section, so the leg's real unbraced
      // length — and hence Euler buckling capacity — is ~1 m, not 3 m. Modelling
      // it at 3 m gave the leg a tiny buckling capacity that the standard guy
      // pretension crushed. This matches the real 45G's braced-leg behaviour.
      baseRadius: 0.264, // 18" face / √3 — CONSTANT section (guyed mast)
      topRadius: 0.264,
      legSection: LEG_45G,
      braceSection: BRACE_45G,
      legMaterial: "a572", // galvanised high-strength round leg
      braceMaterial: "a572",
      bracing: "X", // ROHN Zig-Zag (continuous solid-round) approximated by X-bracing
    },
    // Guy schedule per the 45G catalog: LIGHT EHS strand graduated by height
    // (3/16"–5/16"), NOT the heavy 7/16" a big mast uses. Initial tensions are ~10%
    // of rated breaking strength — a few kN, e.g. 3/16" EHS @ ~400 lb (1.8 kN),
    // 1/4" @ 665 lb (3.0 kN). The earlier version wrongly used uniform 7/16" strand
    // pretensioned to ~9.7 kN each; that ~4× over-tension dumped ~86 kN of pure guy
    // download into the slim legs at ZERO wind, sitting them at their buckling limit
    // and slowly crushing the mast. Real, lighter tensions leave the legs at a
    // sane ~10-15% utilisation. pretensionStrain 0.0005 → T = EA·ε ≈ 2.9 kN (5/16")
    // / 4.0 kN (3/8"). Lower guys carry less, upper guys (longer lever) a touch more.
    guys: [
      { atHeightFrac: 0.11, anchorRadius: 60, section: "guy516", material: "guyStrand", pretensionStrain: 0.0003 },
      { atHeightFrac: 0.25, anchorRadius: 64, section: "guy516", material: "guyStrand", pretensionStrain: 0.0003 },
      { atHeightFrac: 0.44, anchorRadius: 68, section: "guy516", material: "guyStrand", pretensionStrain: 0.0003 },
      { atHeightFrac: 0.67, anchorRadius: 70, section: "guy38", material: "guyStrand", pretensionStrain: 0.0004 },
      { atHeightFrac: 0.89, anchorRadius: 72, section: "guy38", material: "guyStrand", pretensionStrain: 0.0004 },
    ],
    foundation: {
      soil: "stiff-clay",
      base: { type: "pier", width: 0.6, diameter: 0.6, depth: 1.5, concrete: "c20" }, // small central base pier
      anchor: { type: "deadman", width: 1.5, diameter: 0.6, depth: 2.0, concrete: "c20" },
    },
  },
};
